import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail, API_BASE, TOKEN_KEY } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ShieldOff, Copy, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function AdminMFA() {
  const [status, setStatus] = useState(null);
  const [secret, setSecret] = useState("");
  const [qrSrc, setQrSrc] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("idle"); // idle | setup | enabled

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/admin/mfa/status");
      setStatus(data);
      setStage(data.mfa_enabled ? "enabled" : "idle");
    } catch (e) {}
  };
  useEffect(() => { loadStatus(); }, []);

  const beginSetup = async () => {
    try {
      const { data } = await api.post("/admin/mfa/setup");
      setSecret(data.secret);
      // Fetch QR with auth header — convert to blob URL
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API_BASE}/admin/mfa/qr.png`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      setQrSrc(URL.createObjectURL(blob));
      setStage("setup");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const enable = async () => {
    try {
      await api.post("/admin/mfa/enable", { code });
      toast.success("MFA enabled");
      setCode("");
      loadStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const disable = async () => {
    try {
      await api.post("/admin/mfa/disable", { code });
      toast.success("MFA disabled");
      setCode("");
      loadStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <AdminLayout subtitle="Security" title="Multi-Factor Authentication">
      <Card className="rounded-sm border-white/10 bg-white/[0.02] max-w-2xl">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-2">
            {status?.mfa_enabled
              ? <Badge className="bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30 rounded-sm gap-1" data-testid="mfa-status-enabled"><ShieldCheck className="w-3 h-3" /> Enabled</Badge>
              : <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 rounded-sm gap-1" data-testid="mfa-status-disabled"><ShieldOff className="w-3 h-3" /> Disabled</Badge>}
            {status?.mfa_enrolled_at && (
              <span className="text-xs text-zinc-500 font-mono-plex">Since {status.mfa_enrolled_at.slice(0, 10)}</span>
            )}
          </div>
          <p className="text-sm text-zinc-400 leading-relaxed">
            MFA adds a second authentication step using a 6-digit code from your authenticator app (Google Authenticator, Authy, 1Password, Bitwarden). Once enabled, you'll need both your password and a code to sign in to /adentry.
          </p>

          {stage === "idle" && !status?.mfa_enabled && (
            <Button className="mt-6 rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink gap-2" onClick={beginSetup} data-testid="mfa-begin-setup">
              <KeyRound className="w-4 h-4" /> Set up MFA
            </Button>
          )}

          {stage === "setup" && (
            <div className="mt-6 space-y-5 border-t border-white/10 pt-5">
              <div className="grid sm:grid-cols-[200px_1fr] gap-6 items-start">
                {qrSrc && <img src={qrSrc} alt="MFA QR code" className="w-full rounded-sm bg-white p-2" data-testid="mfa-qr" />}
                <div>
                  <div className="eyebrow text-zinc-500">1 · Scan the QR</div>
                  <p className="text-sm text-zinc-400 mt-2">Open your authenticator app and scan this QR. Can't scan? Enter the secret manually:</p>
                  <div className="mt-3 flex items-center gap-2">
                    <code className="font-mono-plex text-xs bg-white/5 border border-white/10 px-3 py-2 rounded-sm flex-1 break-all" data-testid="mfa-secret">{secret}</code>
                    <Button size="sm" variant="outline" className="rounded-sm h-9 border-white/10 text-white hover:bg-white/10" onClick={() => { navigator.clipboard.writeText(secret); toast.success("Copied"); }}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>

              <div>
                <Label className="eyebrow text-zinc-500">2 · Enter the 6-digit code</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="000000"
                    className="rounded-sm h-11 bg-white/5 border-white/10 text-white text-center text-xl tracking-[0.4em] font-mono-plex max-w-[200px]"
                    data-testid="mfa-code-input"
                  />
                  <Button onClick={enable} disabled={code.length !== 6} className="rounded-sm h-11 bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink" data-testid="mfa-enable-button">
                    Enable MFA
                  </Button>
                </div>
              </div>
            </div>
          )}

          {stage === "enabled" && (
            <div className="mt-6 border-t border-white/10 pt-5">
              <Label className="eyebrow text-zinc-500">Disable MFA — enter current 6-digit code</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
                  placeholder="000000"
                  className="rounded-sm h-11 bg-white/5 border-white/10 text-white text-center text-xl tracking-[0.4em] font-mono-plex max-w-[200px]"
                  data-testid="mfa-disable-code-input"
                />
                <Button onClick={disable} disabled={code.length !== 6} variant="outline" className="rounded-sm h-11 border-ec-error/40 text-ec-error hover:bg-ec-error/10" data-testid="mfa-disable-button">
                  Disable MFA
                </Button>
              </div>
              <p className="text-xs text-zinc-500 mt-3">For your safety, disabling MFA requires a valid code from your authenticator. Don't have access? Contact platform support.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
